import { apiService } from './api';
import { User, RegisterData } from '../types';

export interface LoginResponse {
  user: User;
  token: string;
}

export interface RegisterResponse {
  user: User;
  token: string;
}

class AuthService {
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      console.log('[AUTH-SERVICE] Attempting login for:', email);
      // Increase timeout for login requests (30 seconds) to handle slow connections
      const response = await apiService.post<LoginResponse>('/auth/login', {
        email,
        password,
      }, {
        timeout: 30000 // 30 seconds for login
      });

      console.log('[AUTH-SERVICE] Login response received:', {
        success: response.success,
        hasData: !!response.data,
        error: response.error,
        hasToken: !!(response.data as any)?.token,
        userType: (response.data as any)?.user?.userType
      });

      if (!response.success || !response.data) {
        const errorMsg = response.error || 'Login failed';
        console.error('[AUTH-SERVICE] Login failed:', {
          success: response.success,
          error: response.error,
          hasData: !!response.data,
          fullResponse: response
        });
        throw new Error(errorMsg);
      }

      return response.data as any;
    } catch (error: any) {
      console.error('[AUTH-SERVICE] Login exception:', {
        message: error.message,
        status: error.status,
        details: error.details,
        raw: error.raw,
        isApiError: error instanceof Error,
        errorType: error.constructor.name
      });
      throw error;
    }
  }

  async register(userData: RegisterData): Promise<RegisterResponse> {
    const response = await apiService.post<RegisterResponse>('/auth/register', userData);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Registration failed');
    }

    return response.data as any;
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>('/auth/me');

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to get user data');
    }

    return response.data as any;
  }

  async updateUser(userData: Partial<User>): Promise<User> {
    const response = await apiService.put<User>('/auth/profile', userData);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update user');
    }

    return response.data as any;
  }

  async forgotPassword(email: string): Promise<void> {
    const response = await apiService.post('/auth/forgot-password', { email });

    if (!response.success) {
      throw new Error(response.error || 'Failed to send reset email');
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const response = await apiService.post('/auth/reset-password', { token, password });

    if (!response.success) {
      throw new Error(response.error || 'Failed to reset password');
    }
  }

  async verifyEmail(token: string): Promise<void> {
    const response = await apiService.post('/auth/verify-email', { token });

    if (!response.success) {
      throw new Error(response.error || 'Email verification failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout', {});
    } catch {
      // Client still clears token even if server cleanup fails
    }
  }
}

export const authService = new AuthService();
